// © Microsoft Corporation. All rights reserved.

import "../publicPath";

import { CommonHeader, CommonHeaderConfig, CommonHeaderConnector, CommonHeaderReducer, ContextualNavItem } from "@msnews/common-header";
import { ExperienceIds } from "@msnews/experiences-constants";
import { RequestContext, resolver, DynamicBundles } from "@msnews/core";
import { handleAppErrorEventCall } from "../integration/PrimeTelemetry";

import { PageBase } from "@msnews/experiences-redux";
import React from "react";
import { getTrack } from "../integration/PrimeTrack";

// register potential dynamic children of the river.
DynamicBundles.registerExperience(ExperienceIds.meControl, () => import(/* webpackChunkName: "me-control" */ "@msnews/me-control"));

(async () => {
    // Find the host element on the page
    const hostElement = document.getElementById("common-header-experience");
    // Get an instance of the PageBase for this app
    const app = PageBase.getInstance();

    const requestContext = new RequestContext();
    const config = await resolver.getConfig("cms/api/amp/experienceConfigIndex/BB17o60N");
    const configProps = {
        includePCSOnlyFlag: true,
        ...config.properties,
        disableFirstRow: true,
        forwardOcidOnPrimeExternal: true
    } as CommonHeaderConfig;

    const connector = new CommonHeaderConnector("CommonHeader", "", app.rootReducer, new CommonHeaderReducer(), app.store, configProps, requestContext);

    const rootUrl = hostElement.getAttribute("data-root-url");
    const verticalKey = hostElement.getAttribute("data-vertical-key");
    const categoryUrlSegment = hostElement.getAttribute("data-category-url-segment");
    const subCategoryUrlSegment = hostElement.getAttribute("data-subcategory-url-segment");
    const subSubcategoryUrlSegment = hostElement.getAttribute("data-subsubcategory-url-segment");
    const isContextualSecondaryNavEnabled = hostElement.getAttribute("data-is-secondary-nav-enabled") === "1";
    const isRenderingOnMobile = hostElement.getAttribute("data-is-rendering-on-mobile") === "1";

    // disableFirstRow = false when rendering 2 level nav on mobile
    configProps.disableFirstRow = !(isContextualSecondaryNavEnabled && isRenderingOnMobile);

    connector.setOverrideRootUrl(rootUrl);
    connector.setVerticalKeyOverride(verticalKey);
    connector.setIsContextualSecondaryNavEnabled(isContextualSecondaryNavEnabled);

    let primePath = `/${verticalKey}`;
    if (categoryUrlSegment) {
        primePath += `/${categoryUrlSegment.toLowerCase()}`;
        connector.setCategoryUrlSegment(categoryUrlSegment.toLowerCase());
    }

    if (subCategoryUrlSegment) {
        primePath += `/${subCategoryUrlSegment.toLowerCase()}`;
    }

    if (subSubcategoryUrlSegment) {
        primePath += `/${subSubcategoryUrlSegment.toLowerCase()}`;
    }

    connector.setPrimePathOverride(primePath);

    // Setup design system
    const disableDarkmode = hostElement.getAttribute("data-disable-darkmode") === "True";

    // Create prime hybrid tracking functions
    const track = await getTrack();
    const trackEventCb = (element?, action?, behavior?, event?, destination?, headline?) => {
        track.trackEvent(event, element, destination, headline);
    };

    connector.setTrackingFunctions(trackEventCb, handleAppErrorEventCall);

    const CommonHeaderExperience = await connector.connectComponent(CommonHeader, {});

    app.renderRootElement(
        (<CommonHeaderExperience disableDarkmode={disableDarkmode} includeWrapperDesignSystemProvider={true} isContextualSecondaryNavEnabled={isContextualSecondaryNavEnabled}/>),
        hostElement);
})();
